// One GET, its three states, and optionally a poll.
//
// The figures on Today each come from their own endpoint and each fail on
// their own, so each needs the same small machine: loading, an answer, or a
// reason it could not be read. An error is never turned into an empty answer
// here — a screen gets `error` and decides how to say so.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, request, tenantPath } from '../api/client.ts';
import { useWorkspace } from './workspace.tsx';

export interface Read<T> {
  readonly data: T | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly reload: () => Promise<void>;
}

export function describeReadError(caught: unknown, what: string): string {
  if (caught instanceof ApiError) {
    if (caught.isAbsent) return 'That office is not available to this session.';
    if (caught.status === 403) return `Your role cannot read ${what}.`;
    return caught.message;
  }
  return `${what[0]?.toUpperCase() ?? ''}${what.slice(1)} could not be read.`;
}

/**
 * GET `path` under the current office. `path` null means "not yet" (nothing
 * is requested). With `pollMs`, the read repeats while the screen is mounted;
 * a failed poll keeps the last answer on screen and reports the error beside it.
 */
export function useRead<T>(path: string | null, what: string, options: { pollMs?: number } = {}): Read<T> {
  const workspace = useWorkspace();
  const token = workspace.token;
  const tenantId = workspace.membership.tenantId;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const generation = useRef(0);

  const load = useCallback(
    async (background: boolean) => {
      if (path === null) return;
      const mine = ++generation.current;
      if (!background) setLoading(true);
      try {
        const body = await request<T>(tenantPath(tenantId, path), { token });
        if (mine !== generation.current) return;
        setData(body);
        setError(null);
      } catch (caught) {
        if (mine !== generation.current) return;
        if (!background) setData(null);
        setError(describeReadError(caught, what));
      } finally {
        if (mine === generation.current) setLoading(false);
      }
    },
    [path, tenantId, token, what],
  );

  useEffect(() => {
    setData(null);
    void load(false);
    if (options.pollMs === undefined || path === null) return undefined;
    const timer = setInterval(() => void load(true), options.pollMs);
    return () => {
      clearInterval(timer);
      generation.current += 1;
    };
  }, [load, options.pollMs, path]);

  const reload = useCallback(() => load(false), [load]);
  return { data, error, loading, reload };
}

/** `?administrationId=…` for a selection, or '' for all of them. */
export function filterQuery(administrationId: string | undefined, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams(extra);
  if (administrationId !== undefined) params.set('administrationId', administrationId);
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}
